import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, X, Globe, CheckCircle2, XCircle,
  Loader2, Sparkles, Star,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TLD_CHIPS = ['.com', '.io', '.ai', '.app', '.dev', '.co', '.net', '.org'];

const TLD_COLORS = {
  '.com': '#B8CCDF', '.io': '#A78BFA', '.ai': '#F59E0B',
  '.app': '#34D399', '.dev': '#60A5FA', '.co': '#F472B6',
  '.net': '#94A3B8', '.org': '#6EE7B7', '.build': '#FCD34D',
};

function SkeletonCard() {
  return (
    <div className="rounded-xl p-4 animate-pulse" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-4 w-32 rounded" style={{ background: 'var(--orbis-s3)' }} />
          <div className="h-3 w-16 rounded" style={{ background: 'var(--orbis-s2)' }} />
        </div>
        <div className="h-8 w-20 rounded-full" style={{ background: 'var(--orbis-s3)' }} />
      </div>
    </div>
  );
}

function ResultCard({ result, onRegister, purchasing }) {
  const { domain, tld, available, price, renewal_price, is_premium } = result;
  const isPurchasing = purchasing === domain;
  const tldColor = TLD_COLORS[tld] || 'var(--orbis-t3)';

  return (
    <div className="rounded-xl p-4 flex items-center justify-between gap-3 transition-colors"
      style={{
        background: is_premium ? 'rgba(245,158,11,0.04)' : 'var(--orbis-s1)',
        border: `1px solid ${is_premium ? 'rgba(245,158,11,0.25)' : available ? 'var(--orbis-border-md)' : 'var(--orbis-border)'}`,
        opacity: available === false ? 0.5 : 1,
      }}>
      <div className="flex items-center gap-3 min-w-0">
        {available === true && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-positive)' }} />}
        {available === false && <XCircle className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-t4)' }} />}
        {available === null && <Globe className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-t4)' }} />}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium truncate" style={{ fontSize: 14, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>
              {domain.replace(tld, '')}
            </span>
            <span className="px-1.5 py-0.5 rounded text-xs font-semibold shrink-0" style={{ background: `${tldColor}15`, color: tldColor, fontFamily: 'var(--orbis-mono)' }}>
              {tld}
            </span>
            {is_premium && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold shrink-0"
                style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.25)' }}>
                <Star className="w-2.5 h-2.5" />
                Premium
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: available === true ? 'var(--orbis-positive)' : available === false ? 'var(--orbis-t4)' : 'var(--orbis-t3)' }}>
            {available === true
              ? (is_premium ? 'Available — premium registry pricing' : 'Available')
              : available === false ? 'Already registered'
              : 'Availability unknown'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {price && (
          <div className="text-right">
            <p style={{ fontSize: 14, fontWeight: 600, color: is_premium ? '#F59E0B' : 'var(--orbis-t1)' }}>${price}</p>
            <p style={{ fontSize: 10, color: 'var(--orbis-t4)' }}>
              {renewal_price && renewal_price !== price ? `renews $${renewal_price}` : '/year'}
            </p>
          </div>
        )}
        {available !== false && (
          <button
            onClick={() => onRegister(domain)}
            disabled={isPurchasing || available === false}
            className="px-4 py-2 rounded-full text-xs font-medium transition-all"
            style={{
              background: isPurchasing ? 'var(--orbis-s2)' : is_premium ? '#F59E0B' : 'var(--orbis-blue)',
              color: isPurchasing ? 'var(--orbis-t3)' : 'var(--orbis-bg)',
              border: 'none',
              cursor: isPurchasing ? 'not-allowed' : 'pointer',
              minWidth: 80,
            }}
          >
            {isPurchasing ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Register'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DomainSearchModal({ isOpen, onClose, onRegistered }) {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [purchasing, setPurchasing] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery(''); setResults([]); setSearching(false); setPurchasing('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSearch = useCallback(async (q = query) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setSearching(true);
    setResults([]);
    try {
      const { data } = await axios.get(`${API}/api/domains/search`, { params: { query: trimmed } });
      setResults(data);
    } catch (err) {
      toast.error('Search failed — please try again');
    } finally {
      setSearching(false);
    }
  }, [query]);

  const handleChip = (tld) => {
    const base = query.includes('.') ? query.split('.')[0] : query;
    if (!base) return;
    const newQuery = `${base}${tld}`;
    setQuery(newQuery);
    handleSearch(newQuery);
  };

  const handleRegister = useCallback(async (domain) => {
    setPurchasing(domain);
    try {
      const { data } = await axios.post(`${API}/api/domains/register`, { domain });
      if (data.direct_registered) {
        toast.success(`${domain} registered!`);
        if (onRegistered) onRegistered(domain);
        onClose();
      } else if (data.checkout_url) {
        toast.info('Redirecting to checkout…');
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Registration failed';
      toast.error(msg);
    } finally {
      setPurchasing('');
    }
  }, [onClose, onRegistered]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4"
      style={{ background: 'rgba(9,9,11,0.92)', backdropFilter: 'blur(12px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      <div className="w-full max-w-[600px] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)', maxHeight: '80vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: '1px solid var(--orbis-border)' }}>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5" style={{ color: 'var(--orbis-blue)' }} />
            <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-t1)' }}>Register a Domain</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 transition-colors hover:bg-[var(--orbis-s2)]" style={{ color: 'var(--orbis-t3)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 h-12 rounded-xl px-4"
            style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)' }}>
            <Search className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-t3)' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Search for your domain (e.g. acme or acme.com)"
              className="flex-1 bg-transparent outline-none"
              style={{ fontSize: 14, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}
            />
            <button
              onClick={() => handleSearch()}
              disabled={searching || !query.trim()}
              className="px-4 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0"
              style={{ background: 'var(--orbis-blue)', color: 'var(--orbis-bg)', opacity: (!query.trim() || searching) ? 0.5 : 1, cursor: (!query.trim() || searching) ? 'not-allowed' : 'pointer' }}
            >
              {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Search'}
            </button>
          </div>

          {/* TLD chips */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {TLD_CHIPS.map(tld => (
              <button key={tld} onClick={() => handleChip(tld)}
                className="px-2.5 py-1 rounded-full text-xs transition-colors hover:opacity-80"
                style={{ background: `${TLD_COLORS[tld] || '#888'}18`, color: TLD_COLORS[tld] || 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', border: `1px solid ${TLD_COLORS[tld] || '#888'}30` }}>
                {tld}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-2" style={{ scrollbarWidth: 'thin' }}>
          {searching && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}

          {!searching && results.length === 0 && !searching && query && (
            <div className="text-center py-12">
              <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--orbis-t4)' }} />
              <p style={{ color: 'var(--orbis-t3)', fontSize: 14 }}>Press Search or Enter to check availability</p>
            </div>
          )}

          {!searching && results.length === 0 && !query && (
            <div className="text-center py-12">
              <Globe className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--orbis-t4)' }} />
              <p style={{ color: 'var(--orbis-t3)', fontSize: 14 }}>Type a name to search for available domains</p>
              <p style={{ color: 'var(--orbis-t4)', fontSize: 12, marginTop: 6 }}>We'll check .com, .io, .ai and more</p>
            </div>
          )}

          {results.map(r => (
            <ResultCard key={r.domain} result={r} onRegister={handleRegister} purchasing={purchasing} />
          ))}
        </div>
      </div>
    </div>
  );
}
